import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '$components'
import { Card } from '$components'
import { appointmentPublicConfirmMutation } from '$api-client/@tanstack/react-query.gen'
import { Text } from '$components'
import { CheckIcon, WarningIcon, CalendarIcon, LogoIcon } from '$components'
import { ToastHelper } from '$components'

export function ConfirmAppointmentPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const signature = searchParams.get('signature')
  const expires = searchParams.get('expires')

  const { mutate: confirmAppointment, isPending, isSuccess, isError } = useMutation({
    ...appointmentPublicConfirmMutation(),
    onError: (error) => ToastHelper.error(error?.message || 'Failed to confirm appointment. Please contact the business.'),
  })

  const handleConfirm = () => {
    if (!id || !signature || !expires) {
      ToastHelper.error('Invalid or expired link')
      return
    }

    confirmAppointment({
      path: { id: Number(id) },
      body: {
        signature: signature,
        expires: Number(expires),
      },
    })
  }

  useEffect(() => {
    if (!signature || !expires) {
      ToastHelper.error('Invalid or expired link')
    }
  }, [signature, expires])

  const isInvalidLink = !signature || !expires

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Branding */}
        <div className="flex justify-center mb-6">
          <LogoIcon className="h-6 w-auto text-indigo-600" />
        </div>

        {/* Main Card */}
        <Card>
          {!isSuccess && !isError && (
            <Card.Content className="p-8 sm:p-12 border border-neutral-100 rounded-3xl">
              <div className="flex flex-col items-center text-center mb-8">
                <CalendarIcon className="size-10 text-indigo-600 mb-6" />

                <Text
                  type="h2"
                  className="text-gray-900 mb-3"
                >
                  Confirm Your Appointment
                </Text>

                <Text
                  type="s"
                  className="text-gray-600 max-w-sm"
                >
                  Please confirm your attendance to let us know you&apos;ll be there. This helps us prepare for your visit.
                </Text>
              </div>

              <Button
                onClick={handleConfirm}
                isLoading={isPending}
                disabled={isPending || isInvalidLink}
                variant="primary"
                className="w-full h-12 text-base"
              >
                {isPending ? 'Confirming...' : 'Confirm Appointment'}
              </Button>
            </Card.Content>
          )}

          {isSuccess && (
            <Card.Content className="p-8 sm:p-12 flex flex-col border border-neutral-100 items-center text-center rounded-3xl">
              <CheckIcon className="size-10 text-green-400 mb-6" />

              <Text
                type="h2"
                className="text-gray-900 mb-3"
              >
                All Set!
              </Text>

              <Text
                type="s"
                className="text-gray-600 max-w-sm mb-8"
              >
                Your appointment has been successfully confirmed. We look forward to seeing you!
              </Text>
            </Card.Content>
          )}

          {isError && (
            <Card.Content className="p-8 sm:p-12 flex flex-col border border-neutral-100 items-center text-center rounded-3xl">
              <WarningIcon className="size-10 text-red-400 mb-6" />

              <Text
                type="h2"
                className="text-gray-900 mb-3"
              >
                Unable to Confirm
              </Text>

              <Text
                type="s"
                className="text-gray-600 max-w-sm mb-8"
              >
                We couldn&apos;t confirm your appointment. Please contact the business directly for assistance.
              </Text>
            </Card.Content>
          )}
        </Card>

        {/* Footer Text */}
        <div className="text-center">
          <Text
            type="s"
            className="text-gray-600"
          >
            This is a secure confirmation link. Do not share it with others.
          </Text>
        </div>
      </div>
    </div>
  )
}
